/**
 * Newsletter Signup Form - DSGVO Compliant
 * Klaviyo integration matching newsletter-popup functionality
 */

if (!customElements.get('newsletter-signup-form')) {
  class NewsletterSignupForm extends HTMLElement {
    constructor() {
      super();

      // Klaviyo API configuration
      this.klaviyoConfig = {
        companyId: 'Y9pvmG',
        listId: 'SANaye',
        endpoint: 'https://a.klaviyo.com/client/subscriptions/'
      };

      // State
      this.isSubmitting = false;

      // DOM elements
      this.form = this.querySelector('.newsletter-signup-form__form');
      this.emailInput = this.querySelector('.newsletter-signup-form__input');
      this.submitButton = this.querySelector('.newsletter-signup-form__submit');
      this.consentCheckbox = this.querySelector('.newsletter-signup-form__checkbox');
      this.errorContainer = this.querySelector('.newsletter-signup-form__error');
      this.errorMessage = this.errorContainer ? this.errorContainer.querySelector('.error-message') : null;
      this.successContainer = this.querySelector('.newsletter-signup-form__success');

      this.init();
    }

    init() {
      this.bindEvents();
    }

    bindEvents() {
      // Form submission
      if (this.form) {
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.handleSubmit();
        });
      }

      // Real-time email validation
      if (this.emailInput) {
        this.emailInput.addEventListener('input', () => {
          this.clearError();
          if (this.emailInput.classList.contains('invalid')) {
            this.emailInput.classList.remove('invalid');
          }
        });
      }
    }

    async handleSubmit() {
      if (this.isSubmitting) return;

      const email = this.emailInput.value.trim();

      // Validate email
      if (!this.validateEmail(email)) {
        this.showError('Bitte gib eine gültige E-Mail-Adresse ein.');
        this.emailInput.classList.add('invalid');
        this.emailInput.setAttribute('aria-invalid', 'true');
        this.emailInput.focus();
        return;
      }

      // Validate consent checkbox
      if (this.consentCheckbox && !this.consentCheckbox.checked) {
        this.showError('Bitte bestätige deine Einwilligung.');
        this.consentCheckbox.focus();
        return;
      }

      this.isSubmitting = true;
      this.setLoadingState(true);

      try {
        await this.subscribeToKlaviyo(email);
        this.handleSuccess();
      } catch (error) {
        this.handleError(error);
      } finally {
        this.isSubmitting = false;
        this.setLoadingState(false);
      }
    }

    validateEmail(email) {
      const pattern = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;
      return pattern.test(email);
    }

    async subscribeToKlaviyo(email) {
      // Official Klaviyo client-side subscriptions API payload (2024-10-15 revision)
      const payload = {
        data: {
          type: 'subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                  properties: {
                    popup_source: 'newsletter_section'
                  }
                },
                subscriptions: {
                  email: {
                    marketing: {
                      consent: 'SUBSCRIBED'
                    }
                  }
                }
              }
            }
          },
          relationships: {
            list: {
              data: {
                type: 'list',
                id: this.klaviyoConfig.listId
              }
            }
          }
        }
      };

      const url = `${this.klaviyoConfig.endpoint}?company_id=${this.klaviyoConfig.companyId}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          'revision': '2024-10-15'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        if (response.status === 400) {
          throw new Error('INVALID_EMAIL');
        } else if (response.status === 409 || errorData.detail?.includes('already')) {
          throw new Error('ALREADY_SUBSCRIBED');
        } else {
          throw new Error('SERVER_ERROR');
        }
      }

      // 202 Accepted returns empty body, 200 returns JSON
      if (response.status === 202) {
        return { success: true };
      }

      return response.json();
    }

    handleSuccess() {
      // Hide form, show success message
      this.form.classList.add('fade-out');

      setTimeout(() => {
        this.form.classList.add('hidden');
        this.successContainer.classList.remove('hidden');
        this.successContainer.classList.add('fade-in');

        this.announceToScreenReader('Erfolgreich! Bitte bestätige deine E-Mail-Adresse.');
      }, 300);
    }

    handleError(error) {
      let errorMessage;

      switch (error.message) {
        case 'INVALID_EMAIL':
          errorMessage = 'Bitte gib eine gültige E-Mail-Adresse ein.';
          break;
        case 'ALREADY_SUBSCRIBED':
          errorMessage = 'Diese E-Mail-Adresse ist bereits registriert.';
          break;
        case 'SERVER_ERROR':
          errorMessage = 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.';
          break;
        default:
          errorMessage = 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.';
      }

      this.showError(errorMessage);
      this.emailInput.classList.add('invalid');
      this.emailInput.setAttribute('aria-invalid', 'true');
      this.emailInput.focus();
    }

    showError(message) {
      if (this.errorMessage) {
        this.errorMessage.textContent = message;
      }
      if (this.errorContainer) {
        this.errorContainer.classList.remove('hidden');
      }
      this.announceToScreenReader(`Fehler: ${message}`);
    }

    clearError() {
      if (this.errorContainer) {
        this.errorContainer.classList.add('hidden');
      }
      if (this.emailInput) {
        this.emailInput.classList.remove('invalid');
        this.emailInput.setAttribute('aria-invalid', 'false');
      }
    }

    setLoadingState(isLoading) {
      if (!this.submitButton) return;

      if (isLoading) {
        this.submitButton.disabled = true;
        this.submitButton.classList.add('loading');
        this.submitButton.querySelector('.loading-overlay')?.classList.remove('hidden');
        this.announceToScreenReader('Anmeldung wird verarbeitet...');
      } else {
        this.submitButton.disabled = false;
        this.submitButton.classList.remove('loading');
        this.submitButton.querySelector('.loading-overlay')?.classList.add('hidden');
      }
    }

    announceToScreenReader(message) {
      // Create a visually hidden live region announcement
      const announcement = document.createElement('div');
      announcement.setAttribute('role', 'status');
      announcement.setAttribute('aria-live', 'polite');
      announcement.className = 'visually-hidden';
      announcement.textContent = message;

      document.body.appendChild(announcement);

      // Remove after announcement
      setTimeout(() => {
        document.body.removeChild(announcement);
      }, 1000);
    }
  }

  customElements.define('newsletter-signup-form', NewsletterSignupForm);
}
